import {
  IonContent,
  IonInput,
  IonItem,
  IonPage,
  IonText,
  IonTitle,
} from '@ionic/react';
import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  ApplicationLicense,
  ApplicationLicenseType,
  ApplicationVersion,
  ListApplicationByTypeDocument,
  ListApplicationLicenseDocument,
  ListScratchpadAppsDocument,
  Machine,
  useAssignApplicationLicenseMutation,
  useGetAssignedApplicationLicenseQuery,
  useGetLatestApplicationVersionQuery,
  useGetMachineQuery,
} from '../../api/graphql/generated';
import { ApplicationLicenseForm } from '../../components/application/application-license-form';
import { NestedPageHeader } from '../../components/header/page.header';
import { MachineProgramStatus } from '../../components/machine/machine-program-status';
import { SaveStatusToast } from '../../components/toast/toast.gql';
import { FieldLocked } from '../../components/ui/field-locked';
import { useBeaconRouter } from '../../route/route.hooks';

interface RouteParams {
  applicationId: string;
  machineId: string;
}

interface MachineApplicationFormPageProps {
  teamId: string;
}

export const MachineApplicationFormPage = (
  props: MachineApplicationFormPageProps
): ReactElement => {
  const { teamId } = props;
  const { i18n } = useTranslation();
  const params = useParams<RouteParams>();
  const { applicationId, machineId } = params;

  const { data: existingLicenseData } = useGetAssignedApplicationLicenseQuery({
    variables: {
      input: {
        assignedTo: machineId,
        applicationId,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const existingLicense: ApplicationLicense | undefined = useMemo(() => {
    return existingLicenseData?.getAssignedApplicationLicense as ApplicationLicense;
  }, [existingLicenseData?.getAssignedApplicationLicense]);

  const existingLicenseFormData: FormData = useMemo(() => {
    return (
      existingLicenseData?.getAssignedApplicationLicense?.licenseFormValues ||
      {}
    );
  }, [existingLicenseData?.getAssignedApplicationLicense?.licenseFormValues]);

  const { data: latestApplicationVersion } =
    useGetLatestApplicationVersionQuery({
      variables: {
        input: {
          applicationId,
        },
      },
      fetchPolicy: 'cache-and-network',
    });

  const applicationVersion: ApplicationVersion | undefined = useMemo(() => {
    // If an app license is already assigned, use its assigned version
    if (existingLicense?.applicationVersion) {
      return existingLicense.applicationVersion;
    }
    // If the app has not been assigned yet, use the latest version available
    if (latestApplicationVersion?.getLatestApplicationVersion) {
      return latestApplicationVersion.getLatestApplicationVersion as ApplicationVersion;
    }
  }, [latestApplicationVersion, existingLicense?.applicationVersion]);

  const [
    assignApplicationLicense,
    { loading: assignLoading, data: _assignData, error: _assignError },
  ] = useAssignApplicationLicenseMutation({
    refetchQueries: [
      {
        query: ListApplicationLicenseDocument,
        variables: {
          input: {
            assignedTo: machineId,
          },
        },
        fetchPolicy: 'network-only',
      },
      {
        query: ListApplicationByTypeDocument,
        variables: {
          input: {
            licenseType: ApplicationLicenseType.MACHINE,
          },
        },
        fetchPolicy: 'network-only',
      },
      {
        query: ListScratchpadAppsDocument,
        variables: {
          input: {
            machineId,
          },
        },
        fetchPolicy: 'network-only',
      },
    ],
  });

  const licenseForm = useMemo(() => {
    if (applicationVersion) {
      return applicationVersion.licenseForm.form;
    }
  }, [applicationVersion]);

  const formTitle = useMemo(() => {
    return (
      applicationVersion?.application?.name ||
      i18n.t('common:machine_application_default_form_title')
    );
  }, [applicationVersion?.application?.name, i18n]);

  const applicationVersionNumber = useMemo(() => {
    return applicationVersion?.version || i18n.t('common:unknown');
  }, [applicationVersion?.version, i18n]);

  const { data } = useGetMachineQuery({
    variables: { input: { id: machineId, teamId } },
  });

  const { builder } = useBeaconRouter();

  const machine: Machine | undefined = data?.getMachine;
  const onSubmit = useCallback(
    async (formData: FormData) => {
      if (applicationVersion) {
        await assignApplicationLicense({
          variables: {
            input: {
              assignedTo: machineId,
              licenseFormValues: formData,
              applicationVersionId: applicationVersion.id,
            },
          },
        });
      }
    },
    [applicationVersion, assignApplicationLicense, machineId]
  );

  return (
    <IonPage>
      <NestedPageHeader
        defaultBackRoute={builder.getMachineApplicationsRoute(
          teamId,
          machineId
        )}
      >
        <IonTitle>
          {formTitle}
          <div className="subtitle header-program-status">
            <MachineProgramStatus machine={machine} />
          </div>
        </IonTitle>
      </NestedPageHeader>
      <IonContent>
        <IonItem>
          <IonInput
            type="text"
            labelPlacement="stacked"
            readonly={true}
            value={applicationVersionNumber}
          >
            <div slot="label">
              <IonText color="primary">
                {i18n.t('common:app_version')}
                <FieldLocked show={true} />
              </IonText>
            </div>
          </IonInput>
        </IonItem>
        {licenseForm && (
          <ApplicationLicenseForm
            initialFormData={existingLicenseFormData}
            form={licenseForm}
            isLoading={assignLoading}
            onSubmit={onSubmit}
          />
        )}
        <SaveStatusToast loading={assignLoading} />
      </IonContent>
    </IonPage>
  );
};
